import styled, { css } from 'styled-components'
import { ColorName, getColor } from "../theme"

export interface TypographyProps {
    color?: ColorName | "inherit" | string
    as?: "span" | "div" | "p" | "caption" | "li" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
    margin?: boolean
    emphasis?: boolean
    italic?: boolean
    noWrap?: boolean
}

export const Heading1 = styled.h1<TypographyProps>`
    ${props => props.theme.typography.heading1}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary}; 
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Heading2 = styled.h2<TypographyProps>`
    ${props => props.theme.typography.heading2}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary}; 
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Heading3 = styled.h3<TypographyProps>`
    ${props => props.theme.typography.heading3}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary};
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Heading4 = styled.h4<TypographyProps>`
    ${props => props.theme.typography.heading4}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary};
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Heading5 = styled.h4<TypographyProps>`
    ${props => props.theme.typography.heading5}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary};
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Heading6 = styled.h4<TypographyProps>`
    ${props => props.theme.typography.heading6}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary};
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;

export const Body1 = styled.p<TypographyProps>`
    ${props => props.theme.typography.body1}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.primary}; 
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.emphasis ? `font-weight: ${props.theme.typography.bold}` : ""};
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Body2 = styled.p<TypographyProps>`
    ${props => props.theme.typography.body2}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.secondary}; 
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.emphasis ? `font-weight: ${props.theme.typography.bold}` : ""};
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const Body3 = styled.p<TypographyProps>`
    ${props => props.theme.typography.body3}
    color: ${props => getColor(props.color, props.theme) || props.color || props.theme.palette.text.secondary}; 
    margin-bottom: ${props => props.margin ? '' : '0'};   
    ${props => props.emphasis ? `font-weight: ${props.theme.typography.bold}` : ""};
    ${props => props.italic ? "font-style: italic" : ""};
    ${props => props.noWrap ? noWrapCss : ""};
`;
export const ErrorText = styled(Body1)`
    color: ${props => props.theme.palette.danger.main};
`

export const DenseTypography = styled.div`
    font-size: ${props => props.theme.typography.dense};
`;

export const Bold = styled.span`
    font-weight: ${props => props.theme.typography.bold};
`;
export const Italic = styled.span`
    font-style: italic;
`;
export const Code = styled.span`
    display: inline-block;
    ${props => props.theme.typography.code};
    color: ${props => props.theme.palette.text.primary};
    padding: ${props => props.theme.spacing(0, 0.5)};
    border-radius: ${props => props.theme.radius.small};
    border: 1px solid ${props => props.theme.palette.ui.border};
    background: ${props => props.theme.palette.background};
`;
export const CodeBlock = styled.pre`
    ${props => props.theme.typography.code};
    color: ${props => props.theme.palette.text.primary};
    border: 1px solid ${props => props.theme.palette.ui.border};
    border-radius: ${props => props.theme.radius.small};
    padding: ${props => props.theme.spacing(1, 1.5)};
    background: ${props => props.theme.palette.background};
`;
export const Underline = styled.span`
    text-decoration: underline;
`;
export const Strike = styled.span`
    text-decoration: strike;
`
export const BlockQuote = styled.blockquote`
    margin: ${props => props.theme.spacing(1)} 0;
    padding-left: ${props => props.theme.spacing(1)};
    border-left: ${props => props.theme.spacing(0.5)} solid ${props => props.theme.palette.ui.border};
`;
export const LinkText = styled.a`
    // Don't forget you can use the as prop to set the type of element used...
    // an a tag wont be the most appropriate if you're not using a href.
    &:hover {
        text-decoration: underline;
    cursor: pointer;
    }
`;

export const noWrapCss = css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
`

export const NoWrap = styled.span`${noWrapCss}
`;